import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { BrandHead } from './components/BrandHead';
import { Login } from './pages/Login';
import { ServerList } from './pages/ServerList';
import { ServerLayout } from './pages/ServerLayout';
import { ServerDashboard } from './pages/ServerDashboard';
import { ServerActions } from './pages/ServerActions';
import { TicketList } from './pages/TicketList';
import { TicketDetail } from './pages/TicketDetail';
import { Leaderboard } from './pages/Leaderboard';
import { Backups } from './pages/Backups';
import { Giveaways } from './pages/Giveaways';
import { Music } from './pages/Music';
import { TicketsConfig } from './pages/config/TicketsConfig';
import { TempChannelsConfig } from './pages/config/TempChannelsConfig';
import { WelcomeConfig } from './pages/config/WelcomeConfig';
import { LeaveConfig } from './pages/config/LeaveConfig';
import { LoggingConfig } from './pages/config/LoggingConfig';
import { ModMailConfig } from './pages/config/ModMailConfig';
import { LevelingConfig } from './pages/config/LevelingConfig';
import { AutoModConfig } from './pages/config/AutoModConfig';
import { NotificationsConfig } from './pages/config/NotificationsConfig';
import { PatchNotesConfig } from './pages/config/PatchNotesConfig';
import { ServerStatsConfig } from './pages/config/ServerStatsConfig';
import { SelfRolesConfig } from './pages/config/SelfRolesConfig';
import {
  GamesCatalogConfig,
  RulesPanelConfig,
  SupportRulesPanelConfig,
} from './pages/config/GamesConfig';
import { NsfwPanel } from './pages/NsfwPanel';
import { Warnings } from './pages/Warnings';
import { VoicePresence } from './pages/VoicePresence';
import { MessageRate } from './pages/MessageRate';
import { EmbedComposer } from './pages/EmbedComposer';
import { MediaLibrary } from './pages/MediaLibrary';
import { Members } from './pages/Members';
import { Permabans } from './pages/Permabans';
import { CommandPerms } from './pages/CommandPerms';
import { EmojiExplorer } from './pages/EmojiExplorer';
import { AccessPage, ServerAccessPage } from './pages/Access';
import { LogStream } from './pages/LogStream';
import { AdminPanel } from './pages/AdminPanel';
import { ActionLogPage } from './pages/ActionLog';
import { PostedMessagesPage } from './pages/PostedMessages';
import { ProtectionPage } from './pages/Protection';
import './globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Tab return / reconnect reload via lib/live.ts, which spares forms with unsaved input.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrandHead />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<App />}>
            <Route index element={<ServerList />} />
            <Route path="server/:guildId" element={<ServerLayout />}>
              <Route index element={<ServerDashboard />} />
              <Route path="actions" element={<ServerActions />} />
              <Route path="tickets" element={<TicketList />} />
              <Route path="leaderboard" element={<Leaderboard />} />
              <Route path="backups" element={<Backups />} />
              <Route path="giveaways" element={<Giveaways />} />
              <Route path="music" element={<Music />} />
              <Route path="warnings" element={<Warnings />} />
              <Route path="voice" element={<VoicePresence />} />
              <Route path="message-rate" element={<MessageRate />} />
              <Route path="embed" element={<EmbedComposer />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="posts" element={<PostedMessagesPage />} />
              <Route path="members" element={<Members />} />
              <Route path="permabans" element={<Permabans />} />
              <Route path="command-perms" element={<CommandPerms />} />
              <Route path="access" element={<ServerAccessPage />} />
              <Route path="protection" element={<ProtectionPage />} />
              <Route path="config/tickets" element={<TicketsConfig />} />
              <Route path="config/tempchannels" element={<TempChannelsConfig />} />
              <Route path="config/welcome" element={<WelcomeConfig />} />
              <Route path="config/leave" element={<LeaveConfig />} />
              <Route path="config/logging" element={<LoggingConfig />} />
              <Route path="config/modmail" element={<ModMailConfig />} />
              <Route path="config/leveling" element={<LevelingConfig />} />
              <Route path="config/automod" element={<AutoModConfig />} />
              <Route path="config/notifications" element={<NotificationsConfig />} />
              <Route path="config/patch-notes" element={<PatchNotesConfig />} />
              <Route path="config/serverstats" element={<ServerStatsConfig />} />
              <Route path="config/panels/rules" element={<RulesPanelConfig />} />
              <Route path="config/panels/support-rules" element={<SupportRulesPanelConfig />} />
              <Route path="config/panels/self-roles" element={<SelfRolesConfig />} />
              <Route path="config/games-catalog" element={<GamesCatalogConfig />} />
              {/* Legacy URLs from before the Panels-split. */}
              <Route path="config/selfroles" element={<Navigate to="../config/panels/self-roles" replace />} />
              <Route path="config/games" element={<Navigate to="../config/games-catalog" replace />} />
              <Route path="nsfw" element={<NsfwPanel />} />
            </Route>
            <Route path="tickets/:ticketId" element={<TicketDetail />} />
            <Route path="access" element={<AccessPage />} />
            <Route path="whitelist" element={<Navigate to="/access" replace />} />
            <Route path="admin" element={<AdminPanel />} />
            <Route path="admin/actions" element={<ActionLogPage />} />
            <Route path="logs" element={<LogStream />} />
            <Route path="emojis" element={<EmojiExplorer />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
